/*
 * Gift with purchase.
 *
 * Keeps exactly one free campaign gift in the cart while a qualifying product
 * is present, and takes it back out again when it no longer belongs there.
 * The gift is made free by a Shopify automatic "Buy X get Y" discount - this
 * file never touches prices, only line items.
 *
 * How it hangs together:
 *
 *   - sections/api-gift-promotion.liquid renders the authoritative snapshot
 *     (campaign window, gift stock, qualifying/gift lines of the current
 *     cart). snippets/gift-promotion.liquid inlines the same snapshot for the
 *     initial page load.
 *   - theme.js's CartDrawer fires `theme:cart:added` at the end of every cart
 *     build - after add-to-cart, after quantity changes, after removals, on
 *     both /cart and the drawer. That is our single trigger; there is no
 *     polling.
 *   - Between builds the current cart state is read straight out of the DOM
 *     (Liquid stamps data-gift-promo-item / data-gift-promo-qualifying on the
 *     line items), so a pass that has nothing to do costs zero requests. Only
 *     when a change looks necessary do we re-fetch the snapshot and act on it.
 *   - Reconciliations are serialised on one promise chain and every pass is
 *     bounded, so `cart update -> add gift -> cart update -> ...` cannot run
 *     away: the second pass sees the desired state and stops.
 *
 * Shopify limits worth knowing:
 *   - Dynamic checkout buttons on a product page ("Buy it now", Shop Pay,
 *     PayPal) build their own single-product checkout and never touch the
 *     cart, so no theme code can attach the gift to them. The gift applies to
 *     cart based checkouts only. Cart-level checkout is guarded below.
 *   - Inventory is only ever eventually consistent in the storefront. The
 *     snapshot gates the attempt, /cart/add.js is the real arbiter, and a
 *     rejected add parks the campaign instead of retrying.
 */
(function () {
  'use strict';

  var SELECTORS = {
    state: '[data-gift-promotion-state]',
    itemsHolder: '[data-items-holder]',
    drawerTemplate: '[data-cart-drawer-template]',
    cartDrawer: '[data-cart-drawer]',
    cartContainer: '[data-cart-drawer], [data-cart-page]',
    item: '[data-cart-item]',
    quantityField: '[data-quantity-field]',
    checkoutButton: '[data-cart-checkout-button]',
    cartCount: '[data-cart-count]'
  };

  var ATTRIBUTES = {
    giftItem: 'data-gift-promo-item',
    qualifyingItem: 'data-gift-promo-qualifying',
    itemKey: 'data-item',
    itemIndex: 'data-item-index'
  };

  var CLASSES = {
    drawerOpen: 'js-drawer-open-cart'
  };

  var SECTION_ID = 'api-gift-promotion';
  var BLOCK_KEY = 'gift-promotion:blocked';
  var BLOCK_TTL = 30 * 60 * 1000;

  // Bounds. Every one of these is a circuit breaker, not a tuning knob.
  var MAX_STEPS_PER_PASS = 4; // line items fixed within a single reconciliation
  var MAX_ADD_ATTEMPTS = 3; // /cart/add.js calls per page load
  var MAX_MUTATIONS = 8; // cart writes of any kind per page load
  var REBUILD_TIMEOUT = 4000;
  var CHECKOUT_RELEASE_TIMEOUT = 6000;

  var routes = (window.theme && window.theme.routes) || {};
  var cartUrl = routes.cart_url || '/cart';
  var addUrl = (routes.cart_add_url || '/cart/add') + '.js';
  var changeUrl = (routes.cart_change_url || '/cart/change') + '.js';

  // The theme editor re-renders sections constantly and merchants do not
  // expect a preview to write to their cart, so we observe but never mutate.
  var designMode = Boolean(window.Shopify && window.Shopify.designMode);

  var config = null; // campaign settings + gift availability, refreshed on every fetch
  var seedState = null; // the inline snapshot, authoritative for the first pass
  var queue = Promise.resolve();
  var addAttempts = 0;
  var mutations = 0;
  var halted = false;
  var suppressNextDrawerOpen = false;
  var suppressTimer = null;
  var drawerPatched = false;
  var checkoutTimer = null;

  /* --------------------------------------------------------------------- *
   * State
   * --------------------------------------------------------------------- */

  function parseState(root) {
    var node = (root || document).querySelector(SELECTORS.state);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function rememberConfig(state) {
    if (!state) return state;

    config = {
      enabled: state.enabled,
      config_valid: state.config_valid,
      within_window: state.within_window,
      starts_at: state.starts_at,
      ends_at: state.ends_at,
      property: state.property,
      gift: state.gift
    };

    return state;
  }

  function fetchState() {
    return fetch(cartUrl + '?section_id=' + SECTION_ID, {
      headers: {'X-Requested-With': 'XMLHttpRequest'},
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('gift-promotion: section request failed (' + response.status + ')');
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        return rememberConfig(parseState(holder));
      });
  }

  /**
   * Cheap, request-free view of the cart, built from the attributes Liquid
   * stamps on the line items. Before the drawer is opened for the first time
   * those live inside its <template>, which querySelector cannot reach - hence
   * the explicit fallback.
   */
  function readDomState() {
    if (!config) return null;

    var holder = document.querySelector(SELECTORS.itemsHolder);

    if (!holder) {
      var template = document.querySelector(SELECTORS.drawerTemplate);
      holder = template && template.content ? template.content.querySelector(SELECTORS.itemsHolder) : null;
    }

    if (!holder) return null;

    var giftLines = [];
    var qualifyingCount = 0;

    Array.prototype.forEach.call(holder.querySelectorAll(SELECTORS.item), function (item) {
      var quantityField = item.querySelector(SELECTORS.quantityField);
      var quantity = quantityField ? parseInt(quantityField.value, 10) || 0 : 0;

      if (item.hasAttribute(ATTRIBUTES.giftItem)) {
        giftLines.push({
          key: item.getAttribute(ATTRIBUTES.itemKey),
          line: parseInt(item.getAttribute(ATTRIBUTES.itemIndex), 10) || 0,
          quantity: quantity
        });
      } else if (item.hasAttribute(ATTRIBUTES.qualifyingItem)) {
        qualifyingCount += 1;
      }
    });

    return {
      enabled: config.enabled,
      config_valid: config.config_valid,
      within_window: config.within_window,
      starts_at: config.starts_at,
      ends_at: config.ends_at,
      gift: config.gift,
      qualifying_count: qualifyingCount,
      gift_lines: giftLines
    };
  }

  /* --------------------------------------------------------------------- *
   * Decision
   * --------------------------------------------------------------------- */

  /**
   * The campaign window is evaluated in the browser rather than trusting the
   * server verdict alone: the timestamps carry an explicit UTC offset, so this
   * is timezone-proof, and it keeps a page that was opened before the end time
   * (or served from a cache) from handing out gifts afterwards.
   */
  function withinWindow(state) {
    if (!state.config_valid) return false;

    var now = Date.now();
    var start = state.starts_at ? Date.parse(state.starts_at) : NaN;
    var end = state.ends_at ? Date.parse(state.ends_at) : NaN;

    if (state.starts_at && isNaN(start)) return Boolean(state.within_window);
    if (state.ends_at && isNaN(end)) return Boolean(state.within_window);

    if (!isNaN(start) && now < start) return false;
    if (!isNaN(end) && now >= end) return false;

    return true;
  }

  function isBlocked(variantId) {
    try {
      var raw = window.sessionStorage.getItem(BLOCK_KEY);
      if (!raw) return false;

      var blocked = JSON.parse(raw);
      if (blocked.variant_id !== variantId) return false;
      if (Date.now() > blocked.until) {
        window.sessionStorage.removeItem(BLOCK_KEY);
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  function block(variantId) {
    try {
      window.sessionStorage.setItem(BLOCK_KEY, JSON.stringify({variant_id: variantId, until: Date.now() + BLOCK_TTL}));
    } catch (error) {
      // Private mode / storage full - the per-page attempt cap still applies.
    }
  }

  function extraGiftLine(giftLines) {
    return {type: 'remove', line: giftLines[giftLines.length - 1].line};
  }

  /**
   * Returns the single next cart write, or null when the cart is already
   * correct. One step at a time keeps line indexes valid: they shift after
   * every write, so each step is re-decided against a fresh snapshot.
   */
  function decide(state) {
    var giftLines = state.gift_lines || [];
    var gift = state.gift || {};
    var inWindow = Boolean(state.enabled) && withinWindow(state);
    var hasQualifying = state.qualifying_count > 0;

    var wantsGift = inWindow && hasQualifying && gift.configured && gift.in_stock && !isBlocked(gift.variant_id);

    if (wantsGift) {
      if (!giftLines.length) return {type: 'add'};
      if (giftLines.length > 1) return extraGiftLine(giftLines);
      if (giftLines[0].quantity !== 1) return {type: 'quantity', line: giftLines[0].line, quantity: 1};
      return null;
    }

    if (!giftLines.length) return null;

    // A gift that is already in someone's cart is only pulled back out when it
    // stopped being earned - campaign over, feature switched off, or the last
    // qualifying product removed. Running out of stock merely stops new gifts
    // from being handed out; we do take it back if Shopify says the variant
    // can no longer be bought at all, because that would block checkout.
    var mayKeep = inWindow && hasQualifying && gift.purchasable;

    if (mayKeep) {
      if (giftLines.length > 1) return extraGiftLine(giftLines);
      if (giftLines[0].quantity !== 1) return {type: 'quantity', line: giftLines[0].line, quantity: 1};
      return null;
    }

    return {type: 'remove', line: giftLines[0].line};
  }

  /* --------------------------------------------------------------------- *
   * Cart writes
   * --------------------------------------------------------------------- */

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (json) {
          if (!response.ok) {
            var error = new Error(json.description || json.message || response.statusText);
            error.status = response.status;
            throw error;
          }

          return json;
        });
    });
  }

  function applyAction(state, action) {
    if (mutations >= MAX_MUTATIONS) {
      halted = true;
      return Promise.resolve(false);
    }

    mutations += 1;

    if (action.type === 'add') {
      if (addAttempts >= MAX_ADD_ATTEMPTS) {
        halted = true;
        return Promise.resolve(false);
      }

      addAttempts += 1;

      var properties = {};
      properties[state.property || '_promo_gift'] = 'true';

      return postJSON(addUrl, {
        items: [{id: state.gift.variant_id, quantity: 1, properties: properties}]
      })
        .then(function () {
          return true;
        })
        .catch(function (error) {
          // 422 is Shopify telling us the gift sold out between the snapshot
          // and the write. Park the campaign for this shopper instead of
          // hammering /cart/add.js; the next snapshot will confirm it.
          if (error.status === 422) block(state.gift.variant_id);
          return false;
        });
    }

    return postJSON(changeUrl, {
      line: action.line,
      quantity: action.type === 'remove' ? 0 : action.quantity
    })
      .then(function () {
        return true;
      })
      .catch(function () {
        // A failed change is worth another go on the next cart event - the
        // attempt caps above keep that from turning into a retry storm.
        return false;
      });
  }

  /* --------------------------------------------------------------------- *
   * Handing the cart back to the theme
   * --------------------------------------------------------------------- */

  function waitForRebuild() {
    return new Promise(function (resolve) {
      var settled = false;

      var finish = function () {
        if (settled) return;
        settled = true;
        document.removeEventListener('theme:cart:added', finish);
        resolve();
      };

      document.addEventListener('theme:cart:added', finish);
      window.setTimeout(finish, REBUILD_TIMEOUT);
    });
  }

  /**
   * CartDrawer.build() always ends by opening the drawer. That is right when
   * the shopper just added something, and wrong when we quietly reconciled the
   * cart on page load - so the next open call is swallowed on request. The
   * wrapper is installed once and is a no-op unless armed.
   */
  function patchDrawerOpen(cart) {
    if (drawerPatched || typeof cart.openCartDrawer !== 'function') return;

    var original = cart.openCartDrawer.bind(cart);

    cart.openCartDrawer = function () {
      if (suppressNextDrawerOpen) {
        suppressNextDrawerOpen = false;
        window.clearTimeout(suppressTimer);
        return;
      }

      return original();
    };

    drawerPatched = true;
  }

  function armDrawerSuppression() {
    suppressNextDrawerOpen = true;
    window.clearTimeout(suppressTimer);
    // Safety net: never let a swallowed open outlive the build that caused it.
    suppressTimer = window.setTimeout(function () {
      suppressNextDrawerOpen = false;
    }, REBUILD_TIMEOUT + 1000);
  }

  function bumpCartCount(delta) {
    Array.prototype.forEach.call(document.querySelectorAll(SELECTORS.cartCount), function (badge) {
      var count = (parseInt(badge.textContent, 10) || 0) + delta;
      if (count < 0) count = 0;
      badge.textContent = count;
      badge.classList.toggle('is-visible', count > 0);
    });
  }

  /**
   * theme.js hangs its CartDrawer instance on `window.cart`, but only once the
   * header section has booted. Our first pass can land in between, so wait for
   * it - briefly, and only when there is a drawer that needs it.
   */
  function themeCart() {
    var ready = function () {
      return window.cart && typeof window.cart.getCart === 'function';
    };

    if (ready() || !document.querySelector(SELECTORS.cartDrawer)) return Promise.resolve(ready() ? window.cart : null);

    return new Promise(function (resolve) {
      var attempts = 0;
      var poll = window.setInterval(function () {
        attempts += 1;
        if (ready() || attempts > 40) {
          window.clearInterval(poll);
          resolve(ready() ? window.cart : null);
        }
      }, 50);
    });
  }

  /**
   * Re-render the cart through the theme itself so quantity counters, remove
   * buttons, totals and the free shipping bar are all rebound exactly as they
   * are after any other cart change.
   */
  function refreshCart(delta) {
    return themeCart().then(function (cart) {
      return rebuildCart(cart, delta);
    });
  }

  function rebuildCart(cart, delta) {
    var hasCartContainer = Boolean(document.querySelector(SELECTORS.cartContainer));

    if (cart && hasCartContainer) {
      patchDrawerOpen(cart);

      // Leave a drawer that is already open alone; only a reconciliation the
      // shopper did not ask for needs the open call swallowed.
      if (!document.body.classList.contains(CLASSES.drawerOpen)) armDrawerSuppression();

      var rebuilt = waitForRebuild().then(function () {
        // build() opens the drawer immediately after dispatching the event we
        // just awaited, so disarm one task later - never earlier, or a genuine
        // click of the cart toggle could be swallowed instead.
        return new Promise(function (resolve) {
          window.setTimeout(function () {
            suppressNextDrawerOpen = false;
            window.clearTimeout(suppressTimer);
            resolve();
          }, 0);
        });
      });

      cart.getCart();
      return rebuilt;
    }

    // Cart page: the CartDrawer instance lives in theme.js's section registry
    // and is not reachable, but every line item listens for `theme:cart:update`
    // and answers it with the theme's own update + re-render. Re-asserting a
    // line's current quantity is a no-op for Shopify and a full refresh for us.
    var item = document.querySelector(SELECTORS.item);

    if (item) {
      var quantityField = item.querySelector(SELECTORS.quantityField);
      var quantity = quantityField ? parseInt(quantityField.value, 10) || 1 : 1;
      var rebuiltPage = waitForRebuild();

      item.dispatchEvent(
        new CustomEvent('theme:cart:update', {
          detail: {id: item.getAttribute(ATTRIBUTES.itemKey), quantity: quantity}
        })
      );

      return rebuiltPage;
    }

    // No cart UI on this page (cart type "page", shopper is elsewhere). The
    // header badge is the only thing on screen that can go stale.
    if (delta) bumpCartCount(delta);
    return Promise.resolve();
  }

  function lockCheckout(locked) {
    window.clearTimeout(checkoutTimer);

    Array.prototype.forEach.call(document.querySelectorAll(SELECTORS.checkoutButton), function (button) {
      button.disabled = locked;
      if (locked) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });

    // Never let a failed pass leave the shopper unable to check out.
    if (locked) checkoutTimer = window.setTimeout(function () { lockCheckout(false); }, CHECKOUT_RELEASE_TIMEOUT);
  }

  /* --------------------------------------------------------------------- *
   * Reconciliation
   * --------------------------------------------------------------------- */

  function currentState() {
    if (seedState) {
      var state = seedState;
      seedState = null;
      return state;
    }

    return readDomState();
  }

  function run() {
    if (halted || designMode || !config) return Promise.resolve();

    var known = currentState();

    // Nothing to do and we can prove it without asking the server.
    if (known && !decide(known)) return Promise.resolve();

    var delta = 0;
    var changed = false;
    var locked = false;

    var step = function (remaining) {
      if (!remaining || halted) return Promise.resolve();

      return fetchState().then(function (state) {
        if (!state) return;

        var action = decide(state);
        if (!action) return;

        if (!locked) {
          locked = true;
          lockCheckout(true);
        }

        return applyAction(state, action).then(function (ok) {
          if (!ok) return;

          changed = true;
          if (action.type === 'add') delta += 1;
          if (action.type === 'remove') delta -= 1;

          return step(remaining - 1);
        });
      });
    };

    return step(MAX_STEPS_PER_PASS)
      .catch(function (error) {
        // Network trouble is not the shopper's problem: leave the cart exactly
        // as the theme rendered it and try again on the next cart event.
        if (window.console && console.warn) console.warn('gift-promotion:', error.message || error);
      })
      .then(function () {
        if (locked) lockCheckout(false);
        if (!changed) return;
        return refreshCart(delta);
      });
  }

  function schedule() {
    queue = queue.then(run).catch(function () {});
    return queue;
  }

  /* --------------------------------------------------------------------- *
   * Boot
   * --------------------------------------------------------------------- */

  function init() {
    var initial = parseState(document);
    if (!initial) return;

    rememberConfig(initial);
    seedState = initial;

    // Every cart build in theme.js ends with `theme:cart:added` - add to cart,
    // quantity change, removal, upsell, cart page and drawer alike.
    document.addEventListener('theme:cart:added', schedule);
    // First open of the drawer stamps it from a <template> that was rendered
    // with the page; re-check in case the cart moved on since then.
    document.addEventListener('theme:cart:open', schedule);

    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
